import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, Package, FileText, Send,
  Smartphone, ShieldOff, LogOut, Zap, ChevronLeft, ChevronRight,
  Link2, Sparkles, Wallet, Settings2, Tag, Wrench, Mail, Inbox, FileSignature,
  AlertTriangle, Bot, Plug, UserCircle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useState, useEffect } from 'react';
import api from '@/lib/api';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/contacts', icon: Users, label: 'Kontakty' },
  { to: '/segments', icon: Tag, label: 'Segmenty' },
  { to: '/interests', icon: Tag, label: 'Zainteresowania' },
  { to: '/products', icon: Package, label: 'Produkty' },
  { to: '/templates', icon: FileText, label: 'Szablony' },
  { to: '/campaigns', icon: Send, label: 'Kampanie' },
  { to: '/gateways', icon: Smartphone, label: 'Bramki SMS' },
  { to: '/phone-gateways', icon: Smartphone, label: 'Telefony Android' },
  { to: '/external-services', icon: Plug, label: 'Zewn. serwisy (hot leady)' },
  { to: '/promo-codes', icon: Smartphone, label: 'Kody promocyjne' },
  { to: '/blacklist', icon: ShieldOff, label: 'Czarna lista' },
  { to: '/smtp-domains', icon: Mail, label: 'Domeny SMTP' },
  { to: '/mail', icon: Inbox, label: 'Poczta / SMS' },
  { to: '/email-footers', icon: FileSignature, label: 'Stopki emaili' },
  { to: '/tracking', icon: Link2, label: 'Tracking' },
  { to: '/ai', icon: Sparkles, label: 'Asystent AI' },
  { to: '/payouts', icon: Wallet, label: 'Prowizje' },
  { to: '/tools', icon: Wrench, label: 'Narzedzia' },
  { to: '/admin', icon: Settings2, label: 'Admin' },
  { to: '/admin/errors', icon: AlertTriangle, label: 'Monitor bledow' },
  { to: '/admin/agent', icon: Bot, label: 'Agent AI' },
  { to: '/admin/inbox', icon: Inbox, label: 'AI Inbox' },
  { to: '/admin/ai-config', icon: Settings2, label: 'AI providery' },
  { to: '/admin/voice-config', icon: Smartphone, label: 'Voice (Twilio)' },
  { to: '/admin/jobs', icon: Zap, label: 'Task queue' },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadErrors, setUnreadErrors] = useState(0);
  const location = useLocation();

  // Pollowanie liczby nierozwiązanych błędów (tylko admin, co 30s)
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await api.get('/errors/unread-count');
        if (!cancelled) setUnreadErrors(res.data?.unread || 0);
      } catch {
        // ignoruj — badge nie jest krytyczny
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role, location.pathname]);

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`h-screen bg-card border-r border-border flex flex-col transition-[width] duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
      >
        <div className="h-14 flex items-center px-3 gap-2 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Zap className="h-4 w-4 text-primary" />
          </div>
          {!collapsed && <span className="font-heading font-semibold text-base tracking-tight">Torr.pl</span>}
          <Button
            variant="ghost" size="icon"
            className={`ml-auto h-7 w-7 ${collapsed ? 'mx-auto' : ''}`}
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <Separator />
        <ScrollArea className="flex-1 px-2 py-2">
          <nav className="space-y-0.5">
            {navItems.map(item => {
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              const showBadge = item.to === '/admin/errors' && unreadErrors > 0;
              const link = (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`sidebar-nav-${item.to.replace('/', '')}-link`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 relative ${
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  } ${collapsed ? 'justify-center px-0' : ''}`}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {showBadge && !collapsed && (
                    <span className="text-[10px] font-semibold bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {unreadErrors > 99 ? '99+' : unreadErrors}
                    </span>
                  )}
                  {showBadge && collapsed && (
                    <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500" />
                  )}
                </NavLink>
              );
              if (collapsed) {
                return (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            })}
          </nav>
        </ScrollArea>
        <Separator />
        <div className={`p-2 space-y-1 ${collapsed ? 'px-1' : 'px-3'}`}>
          {!collapsed && (
            <Link to="/profile" className="block text-xs text-muted-foreground hover:text-foreground mb-1.5 truncate px-1" title="Moj profil / zmiana hasla">
              {user?.first_name} {user?.last_name}
            </Link>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg text-sm px-2 py-1.5 transition-colors ${
                    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`
                }
                data-testid="sidebar-profile-link"
              >
                <UserCircle className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Moj profil</span>}
              </NavLink>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Moj profil / zmiana hasla</TooltipContent>}
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="sm"
                className={`w-full ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
                onClick={logout}
                data-testid="sidebar-logout-button"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="ml-2">Wyloguj</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Wyloguj</TooltipContent>}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
};
